import React, { useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import Button from "../components/Button";
import { useSchedule } from "../context/ScheduleContext";
import { useNotification } from "../context/NotificationContext";
import { scheduleSession } from "../apiServices/schedule";
import { handleApiError } from "../utils/notificationHelpers";

const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const timeOptions = [
  "9:00",
  "9:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "1:00",
  "1:30",
  "2:00",
  "2:30",
  "3:00",
  "3:30",
  "4:00",
  "4:30",
  "5:00",
  "5:30",
  "6:00",
  "6:30",
  "7:00",
  "7:30",
  "8:00",
  "8:30",
];
const meridians = ["AM", "PM"];

const specializationsData = {
  Depression: [
    "Major Depressive Disorder",
    "Persistent Depressive Disorder (Dysthymia)",
    "Disruptive Mood Dysregulation Disorder",
  ],
  Anxiety: [
    "Separation Anxiety Disorder",
    "Selective Mutism",
    "Specific Phobia",
    "Social Anxiety Disorder (Social Phobia)",
    "Panic Disorder",
    "Agoraphobia",
    "Generalized Anxiety Disorder",
  ],
  ADHD: [
    "ADHD, Predominantly Inattentive Presentation",
    "ADHD, Predominantly Hyperactive-Impulsive Presentation",
    "ADHD, Combined Presentation",
  ],
};

const Home = () => {
  const {
    specialization,
    specializationType,
    selectedSlots,
    updateSpecialization,
    updateSpecializationType,
    updateSlotForDay,
    validateTimeSlot,
    isTimeOverlapping,
  } = useSchedule();

  const { notify } = useNotification();

  const [slots, setSlots] = useState(
    days.reduce(
      (acc, day) => ({ ...acc, [day]: [{ start: "", meridian: "AM" }] }),
      {}
    )
  );
  const [currentDay, setCurrentDay] = useState(null);

  const handleSpecializationChange = (spec) => {
    updateSpecialization(spec);
  };

  const handleSubtypeChange = (subtype) => {
    updateSpecializationType(subtype);
  };

  const handleTimeChange = (day, index, value, type) => {
    setCurrentDay(day);
    const newSlots = { ...slots };

    if (type === "start" || type === "meridian") {
      const newTime = type === "start" ? value : newSlots[day][index].start;
      const newMeridian =
        type === "meridian" ? value : newSlots[day][index].meridian;

      if (!validateTimeSlot(day, newSlots, index, newTime, newMeridian)) {
        return;
      }
    }

    newSlots[day][index][type] = value;
    setSlots(newSlots);

    const hasValidSlot = newSlots[day].some((slot) => slot.start !== "");
    if (hasValidSlot) updateSlotForDay(day, newSlots[day]);
  };

  const addTimeSlot = (day) => {
    setCurrentDay(day);
    const updated = [...slots[day], { start: "", meridian: "AM" }];
    setSlots({ ...slots, [day]: updated });
    updateSlotForDay(day, updated);
  };

  const removeTimeSlot = (day, index) => {
    const updated = [...slots[day]];
    updated.splice(index, 1);
    // If no slots left, add an empty one
    if (updated.length === 0) {
      updated.push({ start: "", meridian: "AM" });
    }
    setSlots({ ...slots, [day]: updated });
    updateSlotForDay(day, updated);
  };

  const isTimeDisabled = (day, currentIndex, time) => {
    const currentSlot = slots[day][currentIndex];
    return isTimeOverlapping(
      day,
      slots,
      currentIndex,
      time,
      currentSlot.meridian
    );
  };

  const canAddSlot = (day) => {
    // Check if the last slot is filled and there are no empty slots
    return slots[day].every((slot) => slot.start !== "");
  };

  const handleSubmit = async () => {
    try {
      if (!specialization) {
        notify.warning("Please select a specialization!");
        return;
      }
      if (!specializationType) {
        notify.warning("Please select a specialization type!");
        return;
      }

      // Check if at least one time slot is selected
      const hasTimeSlots = Object.values(slots).some((daySlots) =>
        daySlots.some((slot) => slot.start !== "")
      );

      if (!hasTimeSlots) {
        notify.warning("Please select at least one time slot!");
        return;
      }

      const formattedSlots = {};
      Object.entries(slots).forEach(([day, times]) => {
        formattedSlots[day] = times
          .map(({ start, meridian }) => {
            if (!start) return null;
            const startIndex = timeOptions.indexOf(start);
            if (startIndex === -1) return null;
            const endIndex = startIndex + 2;
            const end = timeOptions[endIndex] || "12:00";
            let endMeridian = meridian;
            if (start === "11:00" || start === "11:30") {
              endMeridian = meridian === "AM" ? "PM" : "AM";
            }
            return `${start} ${meridian} - ${end} ${endMeridian}`;
          })
          .filter(Boolean);
      });

      await scheduleSession({
        specialization,
        specializationType,
        slots: formattedSlots,
      });
      notify.success("Schedule submitted successfully!");
    } catch (error) {
      handleApiError(error, notify, "schedule");
    }
  };

  return (
    <div className="w-full padding-x mt-10 mb-24 lg:mt-20 lg:mb-36 max-w-none lg:max-w-screen-xl mx-0 lg:mx-auto">
      {/* Responsive container: Full width on mobile, very wide on desktop */}
      <h2 className="text-xl font-semibold mb-4">Doctor's specialization:</h2>
      <div className="flex flex-col gap-6 mb-8 sm:mb-12">
        <div className="flex flex-wrap gap-4 sm:gap-6">
          {Object.keys(specializationsData).map((spec) => (
            <label
              key={spec}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="radio"
                name="specialization"
                value={spec}
                checked={specialization === spec}
                onChange={() => handleSpecializationChange(spec)}
                className="form-radio text-[#fe8235]"
              />
              <span
                className={
                  specialization === spec
                    ? "text-[#fe8235] font-semibold"
                    : "text-gray-800"
                }
              >
                {spec}
              </span>
            </label>
          ))}
        </div>

        {specialization && (
          <div className="w-full max-w-md">
            <select
              value={specializationType}
              onChange={(e) => handleSubtypeChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#fe8235] focus:border-transparent"
            >
              <option value="">Select {specialization} Type</option>
              {specializationsData[specialization].map((subtype) => (
                <option key={subtype} value={subtype}>
                  {subtype}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <h2 className="text-xl font-semibold mb-4">
        Please select a day and time for the session:
      </h2>
      <div className="flex flex-col gap-4 w-full">
        {/* Responsive day/time selector container */}
        {days.map((day) => (
          <div
            key={day}
            className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-6 w-full"
          >
            {/* Responsive: stack day/slots vertically on mobile, horizontally on larger screens */}
            <div
              className={`w-16 sm:w-20 text-center py-1.5 sm:py-2 rounded-lg font-semibold text-base sm:text-lg ${
                slots[day]?.some((s) => s.start !== "")
                  ? "bg-[#fe8235] text-white"
                  : currentDay === day
                  ? "bg-[#fddbb2] text-gray-800"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {day}
            </div>
            <div className="flex gap-2 sm:gap-4 flex-wrap items-center w-full">
              {/* Responsive gap and fill width for slotboxes */}
              {slots[day].map((slot, index) => (
                <div
                  key={index}
                  className="flex items-center bg-white border border-gray-300 shadow-sm px-2 py-1 sm:px-3 sm:py-2 rounded-lg text-xs sm:text-base w-full sm:w-auto max-w-xs gap-2 sm:gap-3"
                >
                  <div className="flex items-center gap-1">
                    <select
                      value={slot.start}
                      onChange={(e) =>
                        handleTimeChange(day, index, e.target.value, "start")
                      }
                      className="outline-none text-xs sm:text-base py-0.5 sm:py-1 rounded-md border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-[#fe8235]"
                    >
                      <option value="">--</option>
                      {timeOptions.map((time) => (
                        <option
                          key={time}
                          value={time}
                          disabled={isTimeDisabled(day, index, time)}
                        >
                          {time}
                        </option>
                      ))}
                    </select>
                    <select
                      value={slot.meridian}
                      onChange={(e) =>
                        handleTimeChange(day, index, e.target.value, "meridian")
                      }
                      className="outline-none text-xs sm:text-base py-0.5 sm:py-1 rounded-md border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-[#fe8235]"
                    >
                      {meridians.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="mx-2 text-base font-medium text-gray-500">
                    -
                  </span>
                  <span className="text-xs sm:text-base min-w-[60px] text-center">
                    {slot.start
                      ? (() => {
                          const startIndex = timeOptions.indexOf(slot.start);
                          if (startIndex === -1) return "--";
                          const endIndex = startIndex + 2;
                          const endTime = timeOptions[endIndex] || "12:00";
                          let endMeridian = slot.meridian;
                          if (slot.start === "11:30") {
                            endMeridian = slot.meridian === "AM" ? "PM" : "AM";
                          }
                          return `${endTime} ${endMeridian}`;
                        })()
                      : "--"}
                  </span>
                  <button
                    onClick={() => removeTimeSlot(day, index)}
                    className="ml-2 text-red-500 hover:text-red-700 text-base sm:text-lg flex items-center justify-center"
                    title="Remove slot"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))}
              <button
                onClick={() => addTimeSlot(day)}
                className={`border px-2 py-0.5 sm:px-3 sm:py-1 rounded-md text-xs sm:text-base ${
                  canAddSlot(day)
                    ? "bg-gray-200 hover:bg-gray-300 cursor-pointer"
                    : "bg-gray-100 cursor-not-allowed opacity-60"
                }`}
                disabled={!canAddSlot(day)}
                title={
                  canAddSlot(day)
                    ? "Add another slot"
                    : "Please fill previous slot first"
                }
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 sm:mt-20 flex justify-center w-full">
        <Button
          text="Submit"
          onClick={handleSubmit}
          className="w-full sm:w-3/5 lg:w-1/3 md:py-4 text-lg sm:text-xl"
        />
      </div>
    </div>
  );
};

export default Home;
